import { Image, MEDIA_BELOW_MD, MEDIA_MD_UP } from "hec/components/image";
import { TextBlock } from "hec/components/text-block";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  ContentButtonNavigation,
} from "hec/components/ui/carousel";
import { useId } from "react";

import type { ITwoColumnProps } from "./types";
import { getResponsiveHeroBgStyles } from "./utils";

const TwoColumn = (props: ITwoColumnProps) => {
  const baseId = useId();
  const {
    backgroundType,
    backgroundImage,
    backgroundImageMobile,
    backgroundColor,
    backgroundGradient,
    heroImage,
    mobileImage,
    external,
    external_link,
    link,
    couponCode,
    enableCouponCode,
    eyebrowText,
    eyebrowIcon,
    eyebrowType,
    disclaimerText,
    headlineType,
    headlineImage,
    headlineText,
    textBlock,
    callToAction,
    buttonColor,
    enableOverlay,
    overlayColor,
    contentPosition,
    textAlignment,
    eyebrowBackgroundColor,
    eyebrowSize,
    eyebrowTextColor,
    carouselItems = [],
    carouselDots,
    priority = false,
  } = props;

  const resolvedLink = external
    ? (external_link?.value?.data?.url ?? "")
    : (link?.value?.data?.url ?? "");

  const heroId = `hero-tc-${baseId.replace(/:/g, "")}`;
  const { style: bgStyle, mobileCss } = getResponsiveHeroBgStyles({
    id: heroId,
    backgroundType,
    backgroundImage,
    backgroundImageMobile,
    backgroundColor,
    backgroundGradient,
  });

  return backgroundType === "carousel" ? (
    <Carousel
      opts={{
        align: "start",
        loop: true,
      }}
      className="w-full"
    >
      <CarouselContent>
        {carouselItems.map((item, index) => {
          const carouselHeroId = `hero-tc-c-${baseId.replace(/:/g, "")}-${index}`;
          const { style: carouselBgStyle, mobileCss: carouselMobileCss } =
            getResponsiveHeroBgStyles({
              id: carouselHeroId,
              backgroundType: item.carouselBackgroundType || "image",
              backgroundImage: item.backgroundImage,
              backgroundImageMobile: item.backgroundImageMobile,
              backgroundColor: item.backgroundColor,
              backgroundGradient: item.backgroundGradient,
            });

          const carouselResolvedLink = item.external
            ? (item.external_link?.value?.data?.url ?? "")
            : (item.link?.value?.data?.url ?? "");

          return (
            <CarouselItem key={index}>
              {carouselMobileCss && <style>{carouselMobileCss}</style>}
              <div
                id={carouselHeroId}
                className="relative flex w-full flex-col justify-center pb-8 pt-8"
                style={carouselBgStyle}
              >
                {/* Desktop layout */}
                <div
                  className={`hidden items-center justify-center gap-2 p-4 md:flex lg:gap-10 ${
                    item.contentPosition === "right"
                      ? "flex-row-reverse"
                      : "flex-row"
                  }`}
                >
                  <div className="flex justify-center">
                    <Image
                      src={item.heroImage || ""}
                      alt="Left Image"
                      className="max-w-[350px] lg:max-w-[480px] xl:max-w-[700px]"
                      priority={priority && index === 0}
                      media={MEDIA_MD_UP}
                      maxCssWidth={700}
                      sizes="(min-width: 1280px) 700px, (min-width: 1024px) 480px, 350px"
                    />
                  </div>
                  <div className="flex flex-col items-center justify-center gap-2 text-center lg:gap-4">
                    <div className="flex flex-col items-center justify-center text-center">
                      <TextBlock
                        couponCode={item.couponCode}
                        enableCouponCode={item.enableCouponCode}
                        eyebrow={item.eyebrowText}
                        eyebrowIcon={item.eyebrowIcon}
                        eyebrowType={item.eyebrowType}
                        disclaimer={item.disclaimerText ?? ""}
                        headlineType={item.headlineType}
                        headlineImage={item.headlineImage}
                        headline={item.headlineText ?? ""}
                        supportText={item.textBlock ?? ""}
                        cta={item.callToAction ?? ""}
                        external={item.external}
                        link={carouselResolvedLink}
                        overlay={item.enableOverlay}
                        overlayColor={item.overlayColor}
                        alignment={item.textAlignment}
                        buttonColor={item.buttonColor}
                        eyebrowBackgroundColor={item.eyebrowBackgroundColor}
                        eyebrowSize={item.eyebrowSize}
                        eyebrowTextColor={item.eyebrowTextColor}
                      />
                    </div>
                  </div>
                </div>

                {/* Mobile layout */}
                <div
                  className={`flex flex-col items-center gap-2 px-4 text-center md:hidden ${
                    item.contentPosition === "right" ? "flex-col-reverse" : ""
                  }`}
                >
                  <Image
                    src={item.mobileImage || ""}
                    alt="Left Image"
                    className={`${
                      item.contentPosition === "right" ? "mt-4" : "mb-4"
                    } w-full max-w-xs`}
                    priority={priority && index === 0}
                    media={MEDIA_BELOW_MD}
                    maxCssWidth={320}
                    sizes="320px"
                  />
                  <TextBlock
                    couponCode={item.couponCode}
                    enableCouponCode={item.enableCouponCode}
                    eyebrow={item.eyebrowText}
                    eyebrowIcon={item.eyebrowIcon}
                    eyebrowType={item.eyebrowType}
                    disclaimer={item.disclaimerText ?? ""}
                    headlineType={item.headlineType}
                    headlineImage={item.headlineImage}
                    headline={item.headlineText ?? ""}
                    supportText={item.textBlock ?? ""}
                    cta={item.callToAction ?? ""}
                    external={item.external}
                    link={carouselResolvedLink}
                    overlay={item.enableOverlay}
                    overlayColor={item.overlayColor}
                    buttonColor={item.buttonColor}
                    alignment={item.textAlignment}
                  />
                </div>
              </div>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      {carouselItems.length > 1 && carouselDots !== false && (
        <ContentButtonNavigation
          buttonType="circleButton"
          data-testid="twocolumn-hero-carousel"
        />
      )}
    </Carousel>
  ) : (
    <>
      {mobileCss && <style>{mobileCss}</style>}
      <div
        id={heroId}
        className="relative flex w-full flex-col justify-center pb-8 pt-8"
        style={bgStyle}
      >
        {/* Desktop layout */}
        <div
          className={`hidden items-center justify-center gap-2 px-8 py-4 md:flex lg:gap-10 lg:p-4 ${
            contentPosition === "right" ? "flex-row-reverse" : "flex-row"
          }`}
        >
          <div className="flex justify-center">
            <Image
              src={heroImage || ""}
              alt="Left Image"
              className="max-w-[280px] lg:max-w-[480px] xl:max-w-[700px]"
              priority={priority}
              media={MEDIA_MD_UP}
              maxCssWidth={700}
              sizes="(min-width: 1280px) 700px, (min-width: 1024px) 480px, 280px"
            />
          </div>
          <div className="flex flex-col items-center justify-center gap-2 text-center lg:gap-4">
            <div className="flex flex-col items-center justify-center text-center">
              <TextBlock
                couponCode={couponCode}
                enableCouponCode={enableCouponCode}
                eyebrow={eyebrowText}
                eyebrowIcon={eyebrowIcon}
                eyebrowType={eyebrowType}
                disclaimer={disclaimerText ?? ""}
                headlineType={headlineType}
                headlineImage={headlineImage}
                headline={headlineText ?? ""}
                supportText={textBlock ?? ""}
                cta={callToAction ?? ""}
                external={external}
                link={resolvedLink}
                overlay={enableOverlay}
                overlayColor={overlayColor}
                alignment={textAlignment}
                buttonColor={buttonColor}
                eyebrowBackgroundColor={eyebrowBackgroundColor}
                eyebrowSize={eyebrowSize}
                eyebrowTextColor={eyebrowTextColor}
              />
            </div>
          </div>
        </div>

        {/* Mobile layout */}
        <div
          className={`flex flex-col items-center gap-2 px-4 text-center md:hidden ${
            contentPosition === "right" ? "flex-col-reverse" : ""
          }`}
        >
          <Image
            src={mobileImage || ""}
            alt="Left Image"
            className={`${
              contentPosition === "right" ? "mt-4" : "mb-4"
            } w-full max-w-xs`}
            priority={priority}
            media={MEDIA_BELOW_MD}
            maxCssWidth={320}
            sizes="320px"
          />
          <TextBlock
            couponCode={couponCode}
            enableCouponCode={enableCouponCode}
            eyebrow={eyebrowText}
            eyebrowIcon={eyebrowIcon}
            eyebrowType={eyebrowType}
            disclaimer={disclaimerText ?? ""}
            headlineType={headlineType}
            headlineImage={headlineImage}
            headline={headlineText ?? ""}
            supportText={textBlock ?? ""}
            cta={callToAction ?? ""}
            external={external}
            link={resolvedLink}
            overlay={enableOverlay}
            overlayColor={overlayColor}
            buttonColor={buttonColor}
            alignment={textAlignment}
          />
        </div>
      </div>
    </>
  );
};

export default TwoColumn;
